/**
 * Add From Price Book Modal
 * 
 * Allows FOH to add items from the Price Book to the current estimate.
 * Implements "snapshot" pricing - prices are captured at the time of addition.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddFromPriceBookModal({ isOpen, onClose, onAddItems, jobGroups = [] }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('packages');
  const [labourRates, setLabourRates] = useState([]);
  const [fees, setFees] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Selected items for each type
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedPartOptions, setSelectedPartOptions] = useState([]);
  const [selectedLabour, setSelectedLabour] = useState(null);
  const [labourHours, setLabourHours] = useState(1);
  const [selectedFee, setSelectedFee] = useState(null);
  const [feeQuantity, setFeeQuantity] = useState(1);
  
  // For adding to existing job group or creating new
  const [targetJobGroup, setTargetJobGroup] = useState('new');
  const [newJobTitle, setNewJobTitle] = useState('');

  // Fetch price book data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [labourRes, feesRes, packagesRes] = await Promise.all([
        fetch(`${API_URL}/api/price-book/labour-rates`),
        fetch(`${API_URL}/api/price-book/fees`),
        fetch(`${API_URL}/api/price-book/packages`),
      ]);
      
      if (labourRes.ok) setLabourRates(await labourRes.json());
      if (feesRes.ok) setFees(await feesRes.json());
      if (packagesRes.ok) setPackages(await packagesRes.json());
    } catch (error) {
      console.error('Error fetching price book:', error);
      toast.error('Failed to load price book');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      // Reset selections
      setSelectedPackage(null);
      setSelectedPartOptions([]);
      setSelectedLabour(null);
      setLabourHours(1);
      setSelectedFee(null);
      setFeeQuantity(1);
      setTargetJobGroup('new');
      setNewJobTitle('');
    }
  }, [isOpen, fetchData]);

  // Calculate totals for preview
  const calculatePackageTotal = (pkg) => {
    if (!pkg) return { labour: 0, parts: 0, total: 0 };
    
    let labour = 0;
    let parts = 0;
    
    if (pkg.pricing_mode === 'calculated' && pkg.calculated_config) {
      const rate = labourRates.find(r => r.code === pkg.calculated_config.labour_rate_code);
      if (rate) {
        labour = rate.rate_ex_vat * pkg.calculated_config.labour_hours;
      }
    } else if (pkg.pricing_mode === 'fixed' && pkg.fixed_config) {
      labour = pkg.fixed_config.fixed_price_ex_vat;
    }
    
    // Add default parts
    if (pkg.default_parts) {
      parts += pkg.default_parts.reduce((sum, p) => sum + (p.default_price_ex_vat * (p.quantity || 1)), 0);
    }
    
    // Add selected part options
    if (pkg.part_options) {
      pkg.part_options.forEach(opt => {
        if (selectedPartOptions.includes(opt.name)) {
          parts += opt.default_price_ex_vat * (opt.quantity || 1);
        }
      });
    }
    
    return { labour, parts, total: labour + parts };
  };

  // Handle package selection
  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    // Pre-select default part options
    const defaults = (pkg.part_options || []).filter(o => o.is_default).map(o => o.name);
    setSelectedPartOptions(defaults);
    setNewJobTitle(pkg.name);
  };

  // Toggle part option selection
  const togglePartOption = (optionName) => {
    setSelectedPartOptions(prev => 
      prev.includes(optionName) 
        ? prev.filter(n => n !== optionName)
        : [...prev, optionName]
    );
  };

  // Add package to estimate
  const handleAddPackage = async () => {
    if (!selectedPackage) return;
    
    try {
      const res = await fetch(`${API_URL}/api/price-book/generate-estimate-lines/${selectedPackage.code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedPartOptions),
      });
      
      if (!res.ok) throw new Error('Failed to generate estimate lines');
      
      const data = await res.json();
      
      // Convert to the format expected by the estimate builder
      const lines = data.lines.map(line => ({
        ...line,
        // Mark as from price book for snapshot tracking
        price_book_source: {
          type: line.source_type,
          code: line.source_code,
          package_code: line.package_code,
          snapshot_at: new Date().toISOString(),
        }
      }));
      
      onAddItems({
        type: 'package',
        title: newJobTitle || selectedPackage.name,
        targetJobGroupId: targetJobGroup !== 'new' ? targetJobGroup : null,
        lines: lines,
        packageCode: selectedPackage.code,
      });
      
      toast.success(`Added ${selectedPackage.name} to estimate`);
      onClose();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Add individual labour
  const handleAddLabour = () => {
    if (!selectedLabour) return;
    
    const line = {
      id: `labour_${Date.now()}`,
      category: 'labour',
      description: selectedLabour.name,
      hours: labourHours,
      rate: selectedLabour.rate_ex_vat,
      qty: labourHours,
      unit_price_ex_vat: selectedLabour.rate_ex_vat,
      vat_rate: selectedLabour.vat_rate,
      price_book_source: {
        type: 'labour_rate',
        code: selectedLabour.code,
        snapshot_at: new Date().toISOString(),
      }
    };
    
    onAddItems({
      type: 'labour',
      title: newJobTitle || selectedLabour.name,
      targetJobGroupId: targetJobGroup !== 'new' ? targetJobGroup : null,
      lines: [line],
    });
    
    toast.success(`Added ${labourHours}hr ${selectedLabour.name}`);
    onClose();
  };

  // Add fee
  const handleAddFee = () => {
    if (!selectedFee) return;
    
    const line = {
      id: `fee_${Date.now()}`,
      category: 'fee',
      description: selectedFee.name,
      qty: feeQuantity,
      unit_price_ex_vat: selectedFee.default_price_ex_vat,
      vat_rate: selectedFee.vat_rate,
      price_book_source: {
        type: 'fee',
        code: selectedFee.code,
        snapshot_at: new Date().toISOString(),
      }
    };
    
    onAddItems({
      type: 'fee',
      title: newJobTitle || selectedFee.name,
      targetJobGroupId: targetJobGroup !== 'new' ? targetJobGroup : null,
      lines: [line],
    });
    
    toast.success(`Added ${selectedFee.name}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" 
      onClick={onClose}
      data-testid="price-book-modal"
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col" 
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Add from Price Book</h2>
              <p className="text-sm text-slate-500">Select items to add to your estimate</p>
            </div>
            <div className="flex items-center gap-2">
              {/* iter199al (Feb 2026) — Quick jump to the editable Price
                  Book admin page. This modal is intentionally read-only
                  (snapshot pricing at add-time), but FOH needed a clear
                  in-context route to update labour rates / fees /
                  packages when something is wrong. Closes the modal
                  before navigating so the FOH doesn't return to a
                  stale snapshot of the rates they just changed. */}
              <button
                type="button"
                onClick={() => { onClose(); navigate("/app/admin/price-book"); }}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 hover:border-blue-300 rounded-md px-3 py-1.5 transition-colors"
                data-testid="pricebook-modal-edit-rates-cta"
                title="Open the Price Book admin to edit labour rates, fees and packages"
              >
                Edit rates & packages →
              </button>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl" data-testid="pricebook-modal-close">&times;</button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {[
              { id: 'packages', label: '📦 Packages', count: packages.length },
              { id: 'labour', label: '⚙️ Labour', count: labourRates.length },
              { id: 'fees', label: '📋 Fees', count: fees.length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`price-book-tab-${tab.id}`}
              >
                {tab.label}
                <span className="ml-1 text-xs opacity-70">({tab.count})</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Packages Tab */}
              {activeTab === 'packages' && (
                <div className="space-y-4">
                  {/* Package List */}
                  <div className="grid gap-3">
                    {packages.map(pkg => (
                      <div 
                        key={pkg.code}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedPackage?.code === pkg.code 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => handleSelectPackage(pkg)}
                        data-testid={`package-option-${pkg.code}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-slate-800">{pkg.name}</h4>
                            <p className="text-sm text-slate-500">{pkg.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-slate-500">{pkg.pricing_mode}</div>
                            {pkg.pricing_mode === 'calculated' && pkg.calculated_config && (
                              <div className="text-sm font-medium text-slate-700">
                                {pkg.calculated_config.labour_hours}hr labour
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Show parts info */}
                        {(pkg.default_parts?.length > 0 || pkg.part_options?.length > 0) && (
                          <div className="mt-2 text-xs text-slate-600">
                            {pkg.default_parts?.length > 0 && (
                              <span className="mr-3">Includes: {pkg.default_parts.map(p => p.name).join(', ')}</span>
                            )}
                            {pkg.part_options?.length > 0 && (
                              <span>Options: {pkg.part_options.map(p => p.name).join(', ')}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Selected Package Configuration */}
                  {selectedPackage && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <h4 className="font-semibold text-slate-800 mb-3">Configure: {selectedPackage.name}</h4>
                      
                      {/* Part Options Selection */}
                      {selectedPackage.part_options?.length > 0 && (
                        <div className="mb-4">
                          <div className="text-sm font-medium text-slate-700 mb-2">Part Options:</div>
                          <div className="flex flex-wrap gap-2">
                            {selectedPackage.part_options.map(opt => (
                              <button
                                key={opt.name}
                                onClick={() => togglePartOption(opt.name)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                  selectedPartOptions.includes(opt.name)
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                {opt.name} - £{opt.default_price_ex_vat.toFixed(2)}
                                {opt.is_default && <span className="ml-1 text-xs opacity-70">(default)</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Price Preview */}
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <div className="text-sm text-slate-600 space-y-1">
                          {(() => {
                            const totals = calculatePackageTotal(selectedPackage);
                            return (
                              <>
                                <div className="flex justify-between">
                                  <span>Labour:</span>
                                  <span className="font-medium">£{totals.labour.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Parts:</span>
                                  <span className="font-medium">£{totals.parts.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-slate-800 font-semibold border-t pt-1 mt-1">
                                  <span>Total (ex VAT):</span>
                                  <span>£{totals.total.toFixed(2)}</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Labour Tab */}
              {activeTab === 'labour' && (
                <div className="space-y-4">
                  <div className="grid gap-2">
                    {labourRates.map(rate => (
                      <div 
                        key={rate.code}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedLabour?.code === rate.code 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => { setSelectedLabour(rate); setNewJobTitle(rate.name); }}
                        data-testid={`labour-option-${rate.code}`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium text-slate-800">{rate.name}</h4>
                            <p className="text-xs text-slate-500 font-mono">{rate.code}</p>
                          </div>
                          <div className="text-lg font-semibold text-slate-800">
                            £{rate.rate_ex_vat.toFixed(2)}/hr
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {selectedLabour && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-slate-700">Hours:</label>
                        <input
                          type="number"
                          min="0.25"
                          step="0.25"
                          value={labourHours}
                          onChange={e => setLabourHours(parseFloat(e.target.value) || 1)}
                          className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="ml-auto text-lg font-semibold text-slate-800">
                          = £{(selectedLabour.rate_ex_vat * labourHours).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Fees Tab */}
              {activeTab === 'fees' && (
                <div className="space-y-4">
                  <div className="grid gap-2">
                    {fees.map(fee => (
                      <div 
                        key={fee.code}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedFee?.code === fee.code 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => { setSelectedFee(fee); setNewJobTitle(fee.name); }}
                        data-testid={`fee-option-${fee.code}`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium text-slate-800">{fee.name}</h4>
                            <p className="text-xs text-slate-500 font-mono">{fee.code}</p>
                          </div>
                          <div className="text-lg font-semibold text-slate-800">
                            £{fee.default_price_ex_vat.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {selectedFee && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-slate-700">Quantity:</label>
                        <input
                          type="number"
                          min="1"
                          value={feeQuantity}
                          onChange={e => setFeeQuantity(parseInt(e.target.value) || 1)}
                          className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="ml-auto text-lg font-semibold text-slate-800">
                          = £{(selectedFee.default_price_ex_vat * feeQuantity).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex-shrink-0 space-y-3">
          {/* Target Selection */}
          {(selectedPackage || selectedLabour || selectedFee) && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-slate-700">Add to:</label>
              <select
                value={targetJobGroup}
                onChange={e => setTargetJobGroup(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="new">➕ Create new job group</option>
                {jobGroups.map(jg => (
                  <option key={jg.id} value={jg.id}>{jg.title}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* New Job Title (if creating new) */}
          {targetJobGroup === 'new' && (selectedPackage || selectedLabour || selectedFee) && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-slate-700">Job Title:</label>
              <input
                type="text"
                value={newJobTitle}
                onChange={e => setNewJobTitle(e.target.value)}
                placeholder="Enter job title..."
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            
            {activeTab === 'packages' && selectedPackage && (
              <button
                onClick={handleAddPackage}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                data-testid="add-package-btn"
              >
                Add Package
              </button>
            )}
            
            {activeTab === 'labour' && selectedLabour && (
              <button
                onClick={handleAddLabour}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                data-testid="add-labour-btn"
              >
                Add Labour
              </button>
            )}
            
            {activeTab === 'fees' && selectedFee && (
              <button
                onClick={handleAddFee}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                data-testid="add-fee-btn"
              >
                Add Fee
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
